import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceTopologyPanel = ({ serviceHealth, onAnalyzeCascadeRisk }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [cascadeAnalysis, setCascadeAnalysis] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      operational: 'border-green-500 bg-green-50',
      degraded: 'border-yellow-500 bg-yellow-50',
      critical: 'border-red-500 bg-red-50',
      maintenance: 'border-blue-500 bg-blue-50'
    };
    return colors?.[status] || 'border-gray-500 bg-gray-50';
  };

  const getStatusIcon = (status) => {
    const icons = {
      operational: 'CheckCircle',
      degraded: 'AlertTriangle',
      critical: 'XCircle',
      maintenance: 'Settings'
    };
    return icons?.[status] || 'Circle';
  };

  const handleAnalyzeCascade = async (service) => {
    setSelectedService(service);
    try {
      const analysis = await onAnalyzeCascadeRisk(service?.name?.toLowerCase()?.replace(/ /g, '_'));
      setCascadeAnalysis(analysis);
    } catch (error) {
      console.error('Cascade analysis error:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Network" className="w-5 h-5 text-blue-600" />
          Service Topology Visualization
        </h3>
        <p className="text-sm text-gray-600 mt-1">Real-time service dependency mapping with cascade failure analysis</p>
      </div>
      {/* Service Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {serviceHealth?.map((service, index) => (
          <div
            key={index}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              getStatusColor(service?.status)
            } ${
              selectedService?.name === service?.name ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => handleAnalyzeCascade(service)}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon
                  name={getStatusIcon(service?.status)}
                  className="w-5 h-5"
                />
                <span className="font-semibold text-gray-900">{service?.name}</span>
              </div>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Response Time</span>
                <span className="font-medium text-gray-900">{service?.responseTime}ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Uptime</span>
                <span className="font-medium text-green-600">{service?.uptime}%</span>
              </div>
            </div>
            <div className="mt-3">
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium uppercase ${
                getStatusColor(service?.status)
              }`}>
                {service?.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Cascade Risk Analysis */}
      {selectedService && cascadeAnalysis && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Cascade Failure Risk Analysis</h4>
            <Button
              onClick={() => {
                setSelectedService(null);
                setCascadeAnalysis(null);
              }}
              variant="outline"
              size="sm"
            >
              <Icon name="X" className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            {/* Selected Service */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="text-sm font-medium text-blue-900 mb-1">Analyzing Service</div>
              <div className="text-lg font-bold text-gray-900">{selectedService?.name}</div>
            </div>

            {/* Risk Level */}
            <div className={`rounded-lg p-4 border-2 ${
              cascadeAnalysis?.riskLevel === 'critical' ?'bg-red-50 border-red-500'
                : cascadeAnalysis?.riskLevel === 'high' ?'bg-orange-50 border-orange-500' :'bg-yellow-50 border-yellow-500'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Cascade Risk Level</div>
                  <div className="text-2xl font-bold uppercase">{cascadeAnalysis?.riskLevel}</div>
                </div>
                <Icon
                  name="AlertTriangle"
                  className={`w-12 h-12 ${
                    cascadeAnalysis?.riskLevel === 'critical' ?'text-red-600'
                      : cascadeAnalysis?.riskLevel === 'high' ?'text-orange-600' :'text-yellow-600'
                  }`}
                />
              </div>
            </div>

            {/* Impact Assessment */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-3">Impact Assessment</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Directly Affected</div>
                  <div className="text-2xl font-bold text-gray-900">{cascadeAnalysis?.directlyAffected?.length || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Total Affected</div>
                  <div className="text-2xl font-bold text-gray-900">{cascadeAnalysis?.totalAffected || 0}</div>
                </div>
              </div>
            </div>

            {/* Affected Services */}
            {cascadeAnalysis?.directlyAffected?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">Directly Affected Services</div>
                <div className="space-y-2">
                  {cascadeAnalysis?.directlyAffected?.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <Icon name="ArrowRight" className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">{service?.label}</span>
                      <span className="text-xs text-gray-600">({service?.type})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="text-sm font-medium text-gray-700 mb-3">Service Status Legend</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm text-gray-700">Operational</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-sm text-gray-700">Degraded</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-sm text-gray-700">Critical</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm text-gray-700">Maintenance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTopologyPanel;
